import { request } from "../api/client";
import { getAccessToken, getAuthSession, hasLocallyValidSession, storeAuthSession } from "./auth-session";

/**
 * Keeps people signed in while they use the app.
 *
 * Puck-Pickup's own session (sessionType "app") lasts weeks. Whenever less than half of it is left, it is traded
 * for a fresh full-length one, so anyone who keeps using the app never gets signed out.
 *
 * Someone who signed in before sessions existed still holds Google's one-hour token. While that is still valid it
 * is traded once for a long session too.
 */

/** Renew once less than this much of a session is left (half of the default 30 days). */
export const RENEW_WHEN_SECONDS_LEFT = 15 * 24 * 60 * 60;

const nowSeconds = () => Math.floor(Date.now() / 1000);

let upgradeAttempted = false;
/** For tests. */
export const resetSessionKeeper = () => {
  upgradeAttempted = false;
};

const storeAppSession = (data) =>
  storeAuthSession({
    access_token: data.access_token,
    token_type: data.token_type,
    expires_in: data.expires_in,
    session_type: data.session_type,
    provider: "google",
  });

/** Trades the current session for a fresh one. Throws if the session is no longer accepted. */
export const renewAppSession = async () => {
  const data = await request("/auth/session/refresh", { method: "POST" });
  return storeAppSession(data);
};

/**
 * Trades a still-valid Google sign-in token for a long session. Only stores the result if the server really
 * issued one: when sessions aren't switched on there, the "new" token is the same Google token, and storing it
 * would wrongly extend how long the app thinks it lasts.
 */
export const upgradeToAppSession = async () => {
  const token = getAccessToken();
  if (!token) return null;
  const data = await request("/auth/google/sign-in", { method: "POST", body: JSON.stringify({ idToken: token }) });
  return data.session_type === "app" ? storeAppSession(data) : null;
};

/**
 * One check. Returns what happened: "none" (not signed in), "fresh" (nothing needed), "renewed", "upgraded",
 * "unchanged" (still on Google's token), "expired" (already past its end) or "failed" (will try again later).
 */
export const keepSessionAlive = async () => {
  const session = getAuthSession();
  if (!session?.accessToken) return "none";

  try {
    if (session.sessionType === "app") {
      const left = session.expiresAt - nowSeconds();
      if (left <= 0) return "expired";
      if (left >= RENEW_WHEN_SECONDS_LEFT) return "fresh";
      await renewAppSession();
      return "renewed";
    }

    if (!hasLocallyValidSession() || upgradeAttempted) return "unchanged";
    upgradeAttempted = true;
    return (await upgradeToAppSession()) ? "upgraded" : "unchanged";
  } catch {
    // A session the server no longer accepts is reported by the request layer. Anything else is a hiccup.
    return "failed";
  }
};
